import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import bg1 from "../../images/background/bg1.jpg";
import bg2 from "../../images/background/bg2.jpg";
import bg3 from "../../images/background/bg3.jpg";
import bg4 from "../../images/background/bg4.jpg";
import bg5 from "../../images/background/bg5.jpg";

interface BackgroundProps {
  totalHeight: number;
};

const bgImages = [bg1, bg2, bg3, bg4, bg5];

const getBackgroundHeight = (totalHeight: number) => {
  // Background needs to cover the viewport bottom even after the parallax offset.
  // At max scroll, top = -maxScrollY/4, so height must be window.innerHeight + maxScrollY/4.
  const maxScrollY = Math.max(0, totalHeight - window.innerHeight);
  return window.innerHeight + Math.ceil(maxScrollY / 4);
};

const getBackgroundScroll = () => {
  return -Math.floor(window.scrollY/4);
};

/**
 * Component to display the background image of the portfolio page.
 */
const Background = ({
  totalHeight
}: BackgroundProps) => {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const [, setResizeTick] = useState(0);
  const backgroundHeight = getBackgroundHeight(totalHeight);

  const [bgImage, setBgImage] = useState<string | null>(null);
  const [backgroundScroll, setBackgroundScroll] = useState<number>(getBackgroundScroll());

  useEffect(() => {
    setBgImage(bgImages[Math.floor(Math.random() * bgImages.length)]);
  }, []);

  /**
   * Handles window's scroll.
   */
  const handleScroll = () => {
    setBackgroundScroll(getBackgroundScroll());
  };

  /**
   * Forces re-render on window resize so getBackgroundHeight
   * recomputes with the current window.innerHeight.
   */
  const handleResize = () => {
    setResizeTick((t) => t + 1);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, {passive: true});
    window.addEventListener("resize", handleResize, {passive: true});

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        zIndex: -50,
        overflow: "hidden",
        // clipPath contains the blur filter output within the element's bounds.
        // overflow:hidden alone doesn't clip filter effects.
        clipPath: "inset(0)",
        filter: "blur(8px)",
        backgroundColor: "background.default",
        backgroundImage: bgImage ? `url("${bgImage}")` : "none",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
        backgroundSize: "cover",
      }}
      style={{
        top: `${backgroundScroll}px`,
        height: `${backgroundHeight}px`,
      }}
      ref={backgroundRef}
    />
  );
};

export default Background;
